import type { z } from 'zod';

import { makeZodSafeFetch, UnauthorizedError } from '@repo/utils/common';

import { appEnv, type AppLocale } from '@/shared/configs/app.config';

/**
 * Builds a locale-aware API URL.
 * @param path - The API path (should start with /)
 * @param locale - The locale to use in the URL
 * @returns The full API URL with locale
 *
 * @example
 * getApiUrl('/menu-tree?type=1', 'sl') // 'https://api.example.com/config/sl/menu-tree?type=1'
 */
export const getApiUrl = (path: string, locale: AppLocale): string => {
  return `${appEnv.VITE_API_URL}/config/${locale}${path}`;
};

/**
 * Runs `fn`; if it throws an {@link UnauthorizedError}, hits the token-refresh
 * endpoint once and retries `fn`. Any other error is rethrown.
 */
export const withTokenRefresh = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof UnauthorizedError) {
      const refreshRes = await fetch(`${appEnv.VITE_API_URL}/api/token/refresh`, {
        credentials: 'include',
      });
      if (!refreshRes.ok) {
        throw error; // or throw new Error('Token refresh failed')
      }

      return await fn();
    }

    throw error;
  }
};

/**
 * Makes a Zod-safe fetch request with token refresh.
 * @param url - The URL to fetch from
 * @param schema - The Zod schema to validate the response against
 * @param options - The request options
 * @returns The validated response data
 */
export const makeZodSafeFetchWithRefreshToken = <TData>(
  url: string,
  schema: z.Schema<TData>,
  options?: RequestInit,
): Promise<TData> => withTokenRefresh(() => makeZodSafeFetch(url, schema, options));

/**
 * Gets the full path of an asset.
 * @param assetPath - The path of the asset
 * @returns The full path of the asset
 */
export const getAssetFullPath = (assetPath: string) => {
  if (assetPath.startsWith('/')) {
    return `${import.meta.env.VITE_ASSETS_URL}${assetPath}`;
  } else if (assetPath.startsWith('http')) {
    return assetPath;
  } else {
    return `${import.meta.env.VITE_ASSETS_URL}/${assetPath}`;
  }
};
